import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'motion/react';
import { RiMailLine, RiArrowLeftLine, RiCheckLine, RiGoogleFill, RiGithubFill } from '@remixicon/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { authClient } from '@/lib/auth-client';

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [socialProvider, setSocialProvider] = useState<string | null>(null);

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setSocialProvider(null);
    setIsSuccess(false);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/password-reset/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: data.email }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Check if user signed up with social auth
        if (result.socialProvider) {
          setSocialProvider(result.socialProvider);
          toast.error(result.message);
        } else {
          toast.error(result.message || 'Failed to send reset email');
        }
        return;
      }

      setIsSuccess(true);
      toast.success('Password reset email sent!');
    } catch (error) {
      toast.error('An unexpected error occurred');
      console.error('Forgot password error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialSignIn = async (provider: 'google' | 'github') => {
    try {
      await authClient.signIn.social({
        provider,
        callbackURL: `${window.location.origin}/dashboard`,
      });
    } catch (error) {
      toast.error(`Failed to sign in with ${provider}`);
      console.error(`${provider} sign in error:`, error);
    }
  };

  const currentTheme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  const logoSrc = currentTheme === 'dark' ? '/moniva-light.svg' : '/moniva-dark.svg';

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-background via-background to-muted/10">
      {/* Left Side - Form */}
      <div className="flex items-center justify-center flex-1 p-8 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <img src={logoSrc} alt="Moniva" className="w-10 h-10" />
            <div>
              <h1 className="text-2xl font-bold">Moniva</h1>
              <p className="text-sm text-muted-foreground">Database Management</p>
            </div>
          </div>

          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="text-2xl">Forgot Password?</CardTitle>
              <CardDescription>
                {isSuccess
                  ? "We've sent you a reset link"
                  : "Enter your email address and we'll send you a password reset link"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-center p-4 rounded-full bg-primary/10 w-fit mx-auto">
                    <RiCheckLine className="size-8 text-primary" />
                  </div>
                  <Alert>
                    <RiMailLine className="h-4 w-4" />
                    <AlertDescription>
                      If an account exists for <strong>{form.getValues('email')}</strong>, you will receive a
                      password reset link shortly. Please check your email inbox (and spam folder).
                    </AlertDescription>
                  </Alert>
                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setIsSuccess(false);
                        form.reset();
                      }}
                    >
                      Send Another Email
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={() => navigate({ to: '/login' })}
                    >
                      <RiArrowLeftLine className="size-4 mr-2" />
                      Back to Sign In
                    </Button>
                  </div>
                </motion.div>
              ) : socialProvider ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  <Alert>
                    <AlertDescription>
                      This account uses{' '}
                      <strong>
                        {socialProvider.charAt(0).toUpperCase() + socialProvider.slice(1)}
                      </strong>{' '}
                      sign-in. There is no password to reset.
                    </AlertDescription>
                  </Alert>
                  <div className="space-y-3">
                    {socialProvider === 'google' && (
                      <Button
                        className="w-full"
                        onClick={() => handleSocialSignIn('google')}
                      >
                        <RiGoogleFill className="size-5 mr-2" />
                        Sign in with Google
                      </Button>
                    )}
                    {socialProvider === 'github' && (
                      <Button
                        className="w-full"
                        onClick={() => handleSocialSignIn('github')}
                      >
                        <RiGithubFill className="size-5 mr-2" />
                        Sign in with GitHub
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setSocialProvider(null);
                        form.reset();
                      }}
                    >
                      Try Another Email
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={() => navigate({ to: '/login' })}
                    >
                      <RiArrowLeftLine className="size-4 mr-2" />
                      Back to Sign In
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email Address</FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="you@example.com"
                                autoComplete="email"
                                disabled={isLoading}
                                autoFocus
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button type="submit" className="w-full" disabled={isLoading} size="lg">
                        {isLoading ? (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            className="w-5 h-5 border-2 rounded-full border-primary-foreground border-t-transparent"
                          />
                        ) : (
                          <>
                            <RiMailLine className="size-4 mr-2" />
                            Send Reset Link
                          </>
                        )}
                      </Button>
                    </form>
                  </Form>

                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => navigate({ to: '/login' })}
                    disabled={isLoading}
                  >
                    <RiArrowLeftLine className="size-4 mr-2" />
                    Back to Sign In
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Additional Help */}
          <div className="mt-6 text-sm text-center text-muted-foreground">
            <p>
              Need help?{' '}
              <a href="mailto:support@usemoniva.com" className="font-medium text-primary hover:underline">
                Contact support
              </a>
            </p>
          </div>
        </motion.div>
      </div>

      {/* Right Side - Illustration */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
        className="items-center justify-center flex-1 hidden p-12 lg:flex bg-gradient-to-br from-primary/5 via-primary/[0.02] to-transparent relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" />
        <div className="max-w-lg space-y-6 text-center relative z-10">
          <motion.img
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            src="/nodata.svg"
            alt="Password Reset"
            className="w-full h-auto opacity-90"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="space-y-3"
          >
            <h3 className="text-2xl font-bold bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Don't Worry, We've Got You
            </h3>
            <p className="text-base text-muted-foreground leading-relaxed">
              Password recovery is quick and secure. We'll send you a reset link to get you back to managing
              your databases in no time.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
